import React from 'react';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SelectableResourceTable, { IColumn } from '../../components/SelectableResourceTable';
import { IFilterField } from 'models/types';
import {
  CompanyDetailedDto,
  OrganisationScoredCompaniesDto,
  PdfStyleDto,
  ReportTypeDto,
} from 'models/dtoTypes';
import {
  GetScoredCompanies,
  PollCompanyScore,
  UpdateScoringOnBehalfOfOrganisation,
} from 'services/openapiClient';
import { utc2str } from 'utils/date';
import { ResetScoreMany, RescoreMany } from 'services/openapiClient';
import downloadKosmosReport from 'utils/downloadKosmosReport';
import CurrentUser from 'auth/currentUser';
import Link from 'components/Link';
import Notice from '../../components/Notice';
import generateNonce from 'utils/generateNonce';
import { pollAsync } from 'utils/polling';
import { GetOrganisationScoredCompany } from 'services/company';
import { useTranslation } from 'react-i18next';
import { getKosmosReportLink, getSgReportLink } from 'utils/report';
const PREFIX = 'AdminScoredCompaniesPage';

const classes = {
  root: `${PREFIX}-root`,
  demo: `${PREFIX}-demo`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`&.${classes.demo}`]: {
    backgroundColor: theme.palette.background.paper,
  },
}));

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

export default function AdminScoredCompaniesPage(): JSX.Element {
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const [nonce, setNonce] = React.useState<string>('');
  const { t } = useTranslation('default');

  const filterSchema: IFilterField[] = [
    {
      label: t('name'),
      name: 'name',
      widget: 'text',
    },
    {
      label: t('external-identifier'),
      name: 'externalIdentifier',
      widget: 'text',
    },
    {
      label: t('organisation'),
      name: 'organisationId',
      widget: 'organisationSelect',
    },
  ];

  const handleRescoreCompany = async (report: OrganisationScoredCompaniesDto) => {
    const companyId = report.companyId || '';
    const organisationId = report.organisationId || '';

    const confirmed = confirm(
      t(
        'rescore-will-erase-the-old-data-and-perform-a-new-scoring-process-it-may-take-a-few-minutes-to-finish-are-you-sure',
      ),
    );
    if (confirmed && companyId && organisationId) {
      try {
        // trigger company update
        const updated = await UpdateScoringOnBehalfOfOrganisation({
          companyId,
          organisationId,
        });

        const { statusCode, errorCode } = updated;
        if (statusCode === 200 || statusCode === 202) {
          setNoticeData({
            message: t('rescoring-is-in-progress-it-might-finish-in-a-few-minutes'),
            severity: 'success',
          });
          const pollFn = async () => {
            return (await PollCompanyScore(companyId)).data;
          };
          const isFinished = (company: CompanyDetailedDto) => {
            return company.scoringState === 'Completed';
          };

          // start polling every 5 seconds
          await pollAsync<CompanyDetailedDto>(pollFn, isFinished, 5);

          // refetch the lastest scored snapshot
          return await GetOrganisationScoredCompany(companyId, organisationId);
        } else {
          // trigger failed, rollback
          if (statusCode === 400 && errorCode === 'DissolvedCompany') {
            setNoticeData({
              message: t('scoring-cannot-be-done-as-company-current-state-is-dissolved'),
              severity: 'error',
            });
          }
        }
      } catch (e) {
        console.error(e);
        setNoticeData({
          message: t('cannot-rescore-the-company'),
          severity: 'error',
        });
      }
    }
    return report;
  };

  const handleDownloadReport = async (
    company: OrganisationScoredCompaniesDto,
    pdfStyle: PdfStyleDto,
    reportType: ReportTypeDto,
  ) => {
    const companyId = company.companyId || '';
    const organisationId = company.organisationId || '';
    try {
      const reportDate = company.scoredAt || companyId || '';
      const name = company.companyName || 'name unidentified';
      const filename = `${name} - Sustainability Report -${reportDate}.pdf`;
      // await savePdf(elementRef.current, filename);
      await downloadKosmosReport({
        companyId,
        organisationId,
        defaultFilename: filename,
        pdfStyle,
        reportType,
      });
      setNoticeData({
        message: t('a-report-should-be-downloaded'),
        severity: 'success',
      });
      return company;
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: t('cannot-download-the-report-for-this-company'),
        severity: 'error',
      });
    }
  };

  const generateColumns = (): IColumn<OrganisationScoredCompaniesDto>[] => [
    {
      label: t('organisation'),
      render: (record) => {
        const orgName = record?.organisationName as string;
        const orgId = record?.organisationId as string;
        if (orgName) {
          return <Link to={`/organisations/${orgId || ''}`} text={orgName} />;
        } else {
          return <></>;
        }
      },
    },
    {
      label: t('company-name'),
      render: (record) => {
        return record?.companyName || t('unknown');
      },
      sortIndex: 'name',
    },
    {
      label: t('country'),
      dataIndex: 'countryCode',
      render: (company) => {
        return company.companyCountry ?? '';
      },
    },
    {
      label: t('external-identifier'),
      render: (company) => {
        if (company.registrationNumbers && company.registrationNumbers.length) {
          return (
            <>
              {' '}
              {company.registrationNumbers.map((item) => (
                <>
                  {' '}
                  {item.typeDescription}: {item.identifier} <br />{' '}
                </>
              ))}{' '}
            </>
          );
        }
        return '';
      },
    },
    {
      label: t('esg-score'),
      render: (report) => {
        if (report && report.score && report.score > 0) {
          const companyId = report.companyId || '';
          const organisationId = report.organisationId || '';
          return (
            <Link
              to={getKosmosReportLink(companyId, organisationId)}
              text={report.score.toString() || ''}
            />
          );
        } else {
          return <span>&nbsp;</span>;
        }
      },
    },
    {
      label: t('sg-score'),
      render: (report) => {
        if (report && report.sgScore && report.sgScore > 0) {
          const companyId = report.companyId || '';
          const organisationId = report.organisationId || '';
          return (
            <Link
              to={getSgReportLink(companyId, organisationId)}
              text={report.sgScore.toString() || ''}
            />
          );
        } else {
          return <span>&nbsp;</span>;
        }
      },
    },
    {
      label: t('scored-at'),
      render: (record) => utc2str(record.scoredAt, 'YYYY-MM-DD HH:mm'),
      sortIndex: 'created',
    },
    {
      label: t('action'),
      actions: [
        {
          label: t('rescore'),
          hide: (report) => !report.companyId,
          onClick: handleRescoreCompany,
        },
        {
          label: t('esg-report'),
          hide: (record) => !record.companyId,
          onClick: (report: OrganisationScoredCompaniesDto) =>
            handleDownloadReport(report, 'CoriolisEsg' as PdfStyleDto, 'Detailed' as ReportTypeDto),
        },
        {
          label: t('evo-summary-report'),
          hide: (record) => !record.companyId || !CurrentUser.getInstance().isAdmin(),
          onClick: (report: OrganisationScoredCompaniesDto) =>
            handleDownloadReport(report, 'EvoEsg' as PdfStyleDto, 'Summary' as ReportTypeDto),
        },
        {
          label: t('evo-report'),
          hide: (record) => !record.companyId || !CurrentUser.getInstance().isAdmin(),
          onClick: (report: OrganisationScoredCompaniesDto) =>
            handleDownloadReport(report, 'EvoEsg' as PdfStyleDto, 'Detailed' as ReportTypeDto),
        },
      ],
    },
  ];

  const handleCloseNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNoticeData(undefined);
  };

  const handleRescoreMany = async (orgScoredCompanyReferenceIds: string[]) => {
    if (!orgScoredCompanyReferenceIds?.length) {
      setNoticeData({
        message: t('none-selected'),
        severity: 'error',
      });
      return;
    }
    const confirmed = confirm(
      `${orgScoredCompanyReferenceIds.length} will be rescored and it may take a few minutes to finish, are you sure?`,
    );
    if (confirmed) {
      try {
        await RescoreMany(orgScoredCompanyReferenceIds);
        setNoticeData({
          message: t('rescoring-is-in-progress-it-might-finish-in-a-few-minutes'),
          severity: 'success',
        });
      } catch (e) {
        console.error(e);
        setNoticeData({
          message: t('there-is-an-error-occur-when-trying-to-rescore-bulk-companies'),
          severity: 'error',
        });
      }
    }
    setNonce(generateNonce());
  };

  const handleResetMany = async (orgScoredCompanyReferenceIds: string[]) => {
    if (!orgScoredCompanyReferenceIds?.length) {
      setNoticeData({
        message: t('none-selected'),
        severity: 'error',
      });
      return;
    }
    const confirmed = confirm(
      `${orgScoredCompanyReferenceIds.length} will be reset and all the score related data will be erased, are you sure?`,
    );
    if (confirmed) {
      try {
        await ResetScoreMany(orgScoredCompanyReferenceIds);
      } catch (e) {
        console.error(e);
        setNoticeData({
          message: t('there-is-an-error-occur-when-trying-to-reset-bulk-companies'),
          severity: 'error',
        });
      }
    }
    setNonce(generateNonce());
  };

  return (
    <Root>
      <h2>{t('all-scored-companies')}</h2>
      <span>
        {t(
          'caution-the-displayed-scores-are-the-latest-for-individuel-organisations-and-rescoring-will-create-a-new-historical-score-snapshot-for-this-organisation-only',
        )}
        .
      </span>
      <SelectableResourceTable
        key={nonce}
        filterSchema={filterSchema}
        columns={generateColumns()}
        batchActions={[
          {
            icon: <DeleteIcon />,
            tooltip: t('Delete'),
            onClick: handleResetMany,
          },
          {
            icon: <RestartAltIcon />,
            tooltip: t('rescore'),
            onClick: handleRescoreMany,
          },
        ]}
        onFetch={GetScoredCompanies}
        defaultQuery={[
          {
            column: 'lastScoredAtStart',
            value: '1970-01-01',
          },
        ]}
        defaultSort={{
          sortColumn: 'created',
          isDesc: true,
        }}
      />
      {noticeData && (
        <Notice
          open={true}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseNotice}
        />
      )}
    </Root>
  );
}
